import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import JsonLd from "@/components/base/JsonLd";
import { getCategoryPageSchema, getOrganizationSchema } from "@/components/base/SchemaOrg";
import { categories, hotContents, latestContents } from "@/mocks/home";
import type { BreadcrumbItem } from "@/components/base/SchemaOrg";

const categoryNameMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryNameMap[c.slug] = c.name;
});

const allContents = [...hotContents, ...latestContents];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = categoryNameMap[slug || ""] || "全部";
  const [activeCategory, setActiveCategory] = useState(slug || "movie");
  const siteUrl = "https://www.91madou.com";

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredContent = allContents.filter((item) => {
    const cat = categories.find((c) => c.name === item.category);
    const catSlug = cat?.slug || "";
    return catSlug === activeCategory;
  });

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { name: "首页", url: siteUrl },
    { name: categoryName, url: `${siteUrl}/category/${activeCategory}` },
  ];

  return (
    <>
      <JsonLd data={getOrganizationSchema()} />
      <JsonLd data={getCategoryPageSchema(activeCategory, breadcrumbs)} />
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="pt-20 pb-8 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">{categoryName}</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-2">
                {categoryName}
              </h1>
              <p className="text-foreground-600 text-sm">
                浏览{categoryName}分类下的精彩影视内容，91麻豆免费视频网站为您提供影视网站免费观看体验
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCategoryChange(cat.slug);
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {filteredContent.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredContent.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.slug}`}
                    className="group block rounded-xl overflow-hidden bg-background-100 transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        title={`${item.title} - ${item.category}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                          {item.rating}
                        </span>
                      </div>
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                        <span className="text-white/80 text-xs">{item.year}</span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1.5 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                        {item.description}
                      </p>
                      <span className="text-xs text-foreground-400 bg-background-200 px-2 py-0.5 rounded">
                        {item.category}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-film-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}